/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/no-redundant-roles */
import { useCallback, useRef } from "react"
import type { ChangeEvent, FC, FocusEvent, KeyboardEvent, ReactElement, RefObject } from "react"

import { AxiosError, HttpStatusCode } from "axios"
import DOMPurify from "dompurify"
import { AnimatePresence, motion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import { type UseFormRegisterReturn, type UseFormReturn, useForm } from "react-hook-form"
import { useIntersection, useMount, useUpdateEffect } from "react-use"

import {
    Button,
    ETextAreaScrollbarVariant,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    TextArea,
    Typography
} from "@/3514/components"
import { useIsMutating } from "@/3514/hooks"
import { asyncCb, fireActionDatadog } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"
import { type TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

import { CoachingChatOwlLetterIcon as LetterIcon } from "../assets"
import { ECoachingChatMutationKey as EMutationKey, coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
import {
    useCoachingChatInputStateSelector as useChatInputUiSelector,
    useCoachingChatInputValueSelector as useChatInputValueSelector,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationErrorSelector as useConversationErrorSelector,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatConversationLastMessageAuthorIsUserSelector as useLastMessageAuthorIsUserSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationErrorVariant as EConversationError,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatInputFormField as EFormField,
    type TCoachingChatInputForm as TForm
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"
import { coachingChatInputFormResolver as formResolver } from "../validation"

const { CHAT_LOCKING_HTTP_ERROR_STATUSES } = uiConfig
const { getChatTransportFromConversationMode: getTransportFromApiMode } = uiUtils
const { withModeInput: withGuard } = guards
const {
    conversation: { input: classes }
} = chatStyles

const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

const FullInput: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        entityId,
        conversation: { messageSenderHook }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const mutationHandler: ReturnType<typeof messageSenderHook> = messageSenderHook({ entityId })

    const { isDisabled: isInputDisabledRaw, isDimmed: isInputDimmedRaw }: ReturnType<typeof useChatInputUiSelector> =
        useChatInputUiSelector(entityId)

    const inputRawValue: string = useChatInputValueSelector(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const status: EConversationStatus = useConversationStatusSelector(entityId)

    const { variant: chatErrorVariant } = useConversationErrorSelector(entityId)

    const isLastMessageAuthoredByUser: boolean = useLastMessageAuthorIsUserSelector(entityId)

    const isMessageRestSending: boolean = useIsMutating(EMutationKey.CreateMessage())

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const hasError: boolean = !isNull(chatErrorVariant)

    const isChatApiFailureError: boolean = eq(chatErrorVariant, EConversationError.ApiFailure)
    const isAiStuckError: boolean = eq(chatErrorVariant, EConversationError.AiStuck)
    const isChatViolationError: boolean = eq(chatErrorVariant, EConversationError.Violation)
    const isStreamingError: boolean = eq(chatErrorVariant, EConversationError.StreamingFailed)

    const {
        setValue: setFieldValue,
        setFocus: setFieldFocus,
        getValues: getFormValues,
        reset: resetForm,
        handleSubmit,
        register: registerInput,
        formState: { isValid: isFormValid }
    }: UseFormReturn<TForm> = useForm<TForm>({
        resolver: formResolver,
        reValidateMode: "onChange",
        criteriaMode: "all",
        defaultValues: { [EFormField.ChatInput]: String() }
    })

    const {
        onBlur: onInputBlur,
        onChange: onInputChange,
        ...registeredInputProps
    }: UseFormRegisterReturn<EFormField.ChatInput> = registerInput(EFormField.ChatInput)

    const wasInputTypedRef: RefObject<boolean> = useRef<boolean>(false)

    const inputViolatedValue: RefObject<string> = useRef<string>(String(""))

    const draftStorageKey: string = SessionStorageServiceKeys.CoachingChatDraft(entityId)

    const persistDraft: (value: string) => void = useCallback(
        (value: string): void => {
            if (isEmpty(value)) sessionStorageService.removeItem(draftStorageKey)
            else sessionStorageService.setItem<string>(draftStorageKey, value)
        },
        [draftStorageKey]
    )

    const formRef: RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null)

    const intersection: IntersectionObserverEntry = useIntersection(formRef, {
        root: null,
        rootMargin: "0px",
        threshold: 1
    })

    const isInputVisible: boolean = !includes<EConversationStatus>(
        [EConversationStatus.NotAvailable, EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
        status
    )

    const isInputDimmed: boolean = isInputDimmedRaw

    const isInputDisabled: boolean = isMessageRestSending || isInputDisabledRaw

    const isButtonDisabled: boolean = Boolean(
        isChatApiFailureError || isChatViolationError || isAiStuckError
            ? eq(getFormValues(EFormField.ChatInput), inputViolatedValue.current) && !isFormValid
            : !isFormValid || isLastMessageAuthoredByUser
    )

    useMount((): void => {
        const draft: string = sessionStorageService.getItem<string>(draftStorageKey)

        if (!isEmpty(draft))
            setFieldValue(EFormField.ChatInput, draft, {
                shouldTouch: true,
                shouldValidate: true
            })
    })

    useUpdateEffect((): void => {
        if (!isLastMessageAuthoredByUser && !hasError && intersection?.isIntersecting)
            setFieldFocus(EFormField.ChatInput)
    }, [isLastMessageAuthoredByUser, intersection?.isIntersecting, hasError, isInputDimmed])

    useUpdateEffect((): void => {
        if (hasError)
            if (!isAiStuckError && !isStreamingError) {
                inputViolatedValue.current = inputRawValue
                setFieldValue(EFormField.ChatInput, inputRawValue, { shouldTouch: true })
                setFieldFocus(EFormField.ChatInput)
            }
    }, [hasError, inputRawValue, isAiStuckError, isStreamingError])

    async function handleMessageSend(values: TForm): Promise<void> {
        if (isButtonDisabled) return void 0

        const sanitizedMessage: string = DOMPurify.sanitize(values[EFormField.ChatInput], {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: []
        })

        if (!isEmpty(sanitizedMessage))
            await mutationHandler(
                { conversationId, message: sanitizedMessage, transport: getTransportFromApiMode(conversationApiMode) },
                async (_data: never, err: AxiosError): Promise<void> => {
                    fireActionDatadog("CoachingChat input submitted", { value: sanitizedMessage })

                    if (isNull(err) || includes<HttpStatusCode>(CHAT_LOCKING_HTTP_ERROR_STATUSES, err?.status)) {
                        resetForm({ [EFormField.ChatInput]: String() })
                        setFieldValue(EFormField.ChatInput, String())
                    }

                    if (isNull(err)) sessionStorageService.removeItem(draftStorageKey)
                }
            )
        else {
            setFieldValue(EFormField.ChatInput, String())
            setFieldFocus(EFormField.ChatInput)
            persistDraft(String(""))
        }

        return void 0
    }

    const handleFormSubmit: TEmptyAsyncCallback = handleSubmit(handleMessageSend)

    const handleInputChange: (e: ChangeEvent) => void = useCallback(
        (e: ChangeEvent): void => {
            void onInputChange(e)
            persistDraft((e.target as HTMLTextAreaElement).value)

            if (!wasInputTypedRef.current) {
                wasInputTypedRef.current = true
                fireActionDatadog("CoachingChat input typing started", {
                    field: EFormField.ChatInput
                })
            }
        },
        [onInputChange, persistDraft]
    )

    const handleInputKeyDown: (e: KeyboardEvent) => void = useCallback(
        (event: KeyboardEvent): Promise<void> => {
            void onInputChange(event)

            if (eq(event.key, "Enter")) {
                event.preventDefault()

                if (!event.shiftKey) handleFormSubmit().then()
                else {
                    const value: string = `${getFormValues(EFormField.ChatInput)} \n`

                    setFieldValue(EFormField.ChatInput, value)
                    persistDraft(value)

                    asyncCb((): void => {
                        event.target.dispatchEvent(new Event("input", { bubbles: true }))
                    })
                }
            }

            return void 0
        },
        [handleFormSubmit, onInputChange, setFieldValue, getFormValues, persistDraft]
    )

    const handleInputFocus: TEmptyCallback = useCallback(
        (): void =>
            void fireActionDatadog("CoachingChat input focused", {
                field: EFormField.ChatInput
            }),
        []
    )

    const handleInputBlur: (e: FocusEvent) => void = useCallback(
        (event: FocusEvent): void => {
            void onInputBlur(event)

            wasInputTypedRef.current = false

            const inputVal: string = getFormValues(EFormField.ChatInput)

            void fireActionDatadog("CoachingChat input blurred", {
                field: EFormField.ChatInput,
                filled: inputVal.length > 0,
                length: inputVal.length
            })
        },
        [onInputBlur, getFormValues]
    )

    return (
        <AnimatePresence initial={false} mode="sync">
            {isInputVisible && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    transition={{ duration: eq(status, EConversationStatus.Paused) ? 0 : 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                    <form
                        role="form"
                        aria-label={t("entities.coachingChat.form.ariaLabel")}
                        aria-describedby="chat-input-hint"
                        ref={formRef}
                        onSubmit={handleFormSubmit}
                        className={classes.field({ isChatLocked: isInputDimmed })}
                    >
                        <label
                            htmlFor={EFormField.ChatInput}
                            className={classes.container()}
                            {...{ "data-rum-ignore": true }}
                        >
                            <TextArea
                                {...registeredInputProps}
                                textColor={ETypographyColor.Black}
                                textSize={ETypographySize.Medium}
                                onKeyDown={handleInputKeyDown}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                className={classes.input()}
                                isDisabled={isInputDisabled}
                                isAreaRequired
                                borderRadius={{ tl: 0, tr: 0, br: 0, bl: 0 }}
                                scrollbarVariant={ETextAreaScrollbarVariant.Thin}
                                withDynamicHeight
                                withNoResize
                                minLines={1}
                                maxLines={4}
                                withNoPaddings
                                withItalicPlaceholder
                                withNoBorder
                                placeholder={t("common.inputPlaceholder")}
                                withSpellingCssOverride
                                enterKeyHint="send"
                            />

                            <div className={classes.infoBox()} id="chat-input-hint">
                                <Typography
                                    text={t("entities.coachingChat.chat.hint")}
                                    color={ETypographyColor.Gray23}
                                    tag={ETypographyTag.Span}
                                    size={ETypographySize.ExtraSmall}
                                    className={classes.hint()}
                                    isItalic
                                />
                                <Button
                                    isContainer
                                    isDisabled={isButtonDisabled}
                                    isBusy={isMessageRestSending}
                                    className={classes.infoBoxButton({
                                        isDisabled: isButtonDisabled,
                                        isStale: isInputDimmed
                                    })}
                                >
                                    <LetterIcon
                                        isAccent={isInputDimmed ? false : isButtonDisabled || isFormValid}
                                        className={classes.icon()}
                                    />
                                </Button>
                            </div>
                        </label>
                    </form>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

const LightInput: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isInputDimmed: boolean = true

    const isButtonDisabled: boolean = true

    return (
        <form className={classes.field({ isChatLocked: isInputDimmed })}>
            <div className={classes.container()}>
                <TextArea
                    name={EFormField.ChatInput}
                    textColor={ETypographyColor.Black}
                    textSize={ETypographySize.Medium}
                    className={classes.input()}
                    isDisabled
                    borderRadius={{ tl: 0, tr: 0, br: 0, bl: 0 }}
                    scrollbarVariant={ETextAreaScrollbarVariant.Thin}
                    withDynamicHeight
                    withNoResize
                    minLines={1}
                    maxLines={4}
                    withNoPaddings
                    withItalicPlaceholder
                    withNoBorder
                    placeholder={t("common.inputPlaceholder")}
                />

                <div className={classes.infoBox()}>
                    <Typography
                        text={t("entities.coachingChat.chat.hint")}
                        color={ETypographyColor.Gray23}
                        tag={ETypographyTag.Span}
                        size={ETypographySize.ExtraSmall}
                        className={classes.hint()}
                        isItalic
                    />
                    <Button
                        isContainer
                        isDisabled={isButtonDisabled}
                        className={classes.infoBoxButton({ isDisabled: isButtonDisabled, isStale: isInputDimmed })}
                    >
                        <LetterIcon className={classes.icon()} />
                    </Button>
                </div>
            </div>
        </form>
    )
}

const GuardedInput: FC = withGuard(FullInput, LightInput)

GuardedInput.displayName = "CoachingChatInput"

export { GuardedInput as CoachingChatInput }
